import React from 'react'
import { Box, TextField, IconButton, Typography, Tooltip } from '@mui/material'
import { styled } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import {
  SoapTemplateItem as SoapItem,
  SoapType,
} from '@/features/internal/drugs/types/soapTemplate'
import { SECONDARY_ICON_BUTTON_STYLE } from '@/shared/constants/buttonStyles'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'

// SOAP種別によって色を変更する
const getSoapTypeColor = (soapType: SoapType): string => {
  switch (soapType) {
    case 'S':
      return colorTokens.soapType.S
    case 'O':
      return colorTokens.soapType.O
    case 'A':
      return colorTokens.soapType.A
    case 'OP':
      return colorTokens.soapType.OP
    case 'EP':
    case 'CP':
      return colorTokens.soapType.EP_CP
    default:
      return colorTokens.soapType.default
  }
}

// SOAP種別の日本語表記を取得
const getSoapTypeLabel = (soapType: SoapType): string => {
  switch (soapType) {
    case 'S':
      return 'Subjective（主観的情報）'
    case 'O':
      return 'Objective（客観的情報）'
    case 'A':
      return 'Assessment（評価）'
    case 'OP':
      return 'Observation Plan（観察計画）'
    case 'EP':
      return 'Education Plan（教育計画）'
    case 'CP':
      return 'Care Plan（ケア計画）'
    default:
      return soapType
  }
}

// SOAP種別を表示するためのスタイル付きコンポーネント
const SoapTypeLabel = styled(Box)<{ soaptype: SoapType }>(
  ({ theme, soaptype }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: getSoapTypeColor(soaptype),
    color: theme.palette.getContrastText(getSoapTypeColor(soaptype)),
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    minWidth: 40,
    fontWeight: 'bold',
    textAlign: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  })
)

interface SoapTemplateItemProps {
  /**
   * SOAP項目データ
   */
  item: SoapItem

  /**
   * 編集モードかどうか
   */
  editMode?: boolean

  /**
   * テンプレート内容が変更されたときに呼び出されるコールバック関数
   */
  onContentChange: (content: string) => void

  /**
   * 削除ボタンがクリックされたときに呼び出されるコールバック関数
   */
  onDelete: () => void
}

/**
 * SOAPテンプレートの個別項目を表示・編集するコンポーネント
 */
const SoapTemplateItem: React.FC<SoapTemplateItemProps> = ({
  item,
  editMode = false,
  onContentChange,
  onDelete,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        mb: 1,
        width: '100%',
      }}
    >
      <SoapTypeLabel soaptype={item.soap_type}>{item.soap_type}</SoapTypeLabel>

      {editMode ? (
        <TextField
          fullWidth
          multiline
          variant="outlined"
          size="small"
          value={item.content}
          onChange={(e) => onContentChange(e.target.value)}
          placeholder={`${getSoapTypeLabel(item.soap_type)}を入力`}
          sx={{
            ml: 1,
            flexGrow: 1,
          }}
          aria-label={`${getSoapTypeLabel(item.soap_type)}の内容`}
        />
      ) : (
        <Typography
          variant="body1"
          sx={{
            ml: 1,
            flexGrow: 1,
            whiteSpace: 'pre-wrap',
          }}
        >
          {item.content}
        </Typography>
      )}

      {editMode && (
        <Tooltip title="削除" placement="top">
          <IconButton
            onClick={() => {
              if (window.confirm('このSOAP項目を削除しますか？')) {
                onDelete()
              }
            }}
            size="small"
            sx={{
              ml: 1,
              ...SECONDARY_ICON_BUTTON_STYLE,
            }}
            aria-label="SOAP項目を削除"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  )
}

export default SoapTemplateItem
